import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaPython, FaBrain, FaDesktop, FaRobot } from 'react-icons/fa';

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

const features = [
  '隱私優先設計，資料不離開本機',
  '多模型支援（可自由切換 LLM）',
  '電腦控制與自動化操作',
  '訊息整合與瀏覽器 GUI',
];

const images = [
  { src: '/images/openkuro_01.png', alt: 'OpenKuro 實機畫面 1' },
  { src: '/images/openkuro_02.png', alt: 'OpenKuro 實機畫面 2' },
  { src: '/images/openkuro_03.png', alt: 'OpenKuro 實機畫面 3' },
];

const OpenKuroShowcase = () => {
  const [activeImage, setActiveImage] = useState(0);

  return (
    <motion.div
      variants={itemVariants}
      className="mb-16 rounded-2xl border border-primary-800/30 bg-gradient-to-br from-slate-800/60 to-slate-900/60 overflow-hidden"
    >
      {/* 頂部標籤列 */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <span className="inline-block px-3 py-0.5 rounded-full bg-primary-900/50 text-primary-400 text-xs font-medium border border-primary-800/40">
            精選展示
          </span>
          <h3 className="text-lg font-bold">OpenKuro</h3>
        </div>
        <a
          href="https://github.com/kaikaichumi/OpenKuro"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-800/80 text-sm font-medium hover:bg-primary-600 transition-colors"
        >
          <FaGithub />
          GitHub
        </a>
      </div>

      <div className="grid lg:grid-cols-3 gap-0">
        {/* 左側：專案資訊 */}
        <div className="p-6 border-r border-white/10">
          <p className="text-gray-300 text leading-relaxed mb-5">
            隱私優先的個人 AI 助理，支援多種 LLM 模型切換，整合電腦控制、訊息發送與瀏覽器 GUI，讓 AI 完全運行於本機。
          </p>

          <ul className="space-y-2.5 mb-6">
            {features.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5 text-gray-300 text">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0"></span>
                {f}
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-2">
            {[
              { icon: <FaPython className="text-blue-300" />, label: 'Python' },
              { icon: <FaBrain className="text-purple-400" />, label: 'Multi-LLM' },
              { icon: <FaDesktop className="text-sky-400" />, label: 'Computer Control' },
              { icon: <FaRobot className="text-green-400" />, label: 'Privacy-First' },
            ].map(({ icon, label }) => (
              <span key={label} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-800/60 text-xs font-medium">
                {icon} {label}
              </span>
            ))}
          </div>

          {/* 縮圖選擇器 */}
          <div className="flex gap-2 mt-6">
            {images.map((img, i) => (
              <motion.button
                key={i}
                onClick={() => setActiveImage(i)}
                className={`relative flex-1 overflow-hidden rounded-lg border-2 transition-colors ${
                  activeImage === i ? 'border-primary-500' : 'border-white/10 opacity-60'
                }`}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
              >
                <img src={img.src} alt={img.alt} className="w-full h-auto object-cover" />
              </motion.button>
            ))}
          </div>
        </div>

        {/* 右側：大圖展示 */}
        <div className="lg:col-span-2 p-6 flex items-center justify-center bg-slate-900/30">
          <motion.div
            key={activeImage}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="w-full rounded-xl overflow-hidden border border-white/10 shadow-2xl shadow-black/40"
          >
            <img
              src={images[activeImage].src}
              alt={images[activeImage].alt}
              className="w-full h-auto"
            />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default OpenKuroShowcase;
